"use client";

import { motion, AnimatePresence } from "motion/react";
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

type Item = { q: string; a: string; category?: string };

export function AccordionBlock({ items, title, eyebrow, description, defaultOpen = 0, searchable = false }: { items: Item[]; title?: string; eyebrow?: string; description?: string; defaultOpen?: number; searchable?: boolean }) {
  const [open, setOpen] = useState<number | null>(defaultOpen);
  const [query, setQuery] = useState("");
  const filtered = query ? items.filter((i) => i.q.toLowerCase().includes(query.toLowerCase()) || i.a.toLowerCase().includes(query.toLowerCase())) : items;

  return (
    <div className="space-y-8">
      {(eyebrow || title || description) && (
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mx-auto max-w-3xl text-center">
          {eyebrow && <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-amber-gold-500">{eyebrow}</div>}
          {title && <h2 className="font-rajdhani text-3xl font-bold text-foreground lg:text-4xl">{title}</h2>}
          {description && <p className="mt-3 text-foreground/80/80">{description}</p>}
        </motion.div>
      )}
      {searchable && (
        <div className="mx-auto max-w-xl">
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…" className="w-full rounded-xl border border-border bg-muted/70 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-amber-gold-500 focus:outline-none" />
        </div>
      )}
      <div className="space-y-3">
        {filtered.map((item, i) => {
          const isOpen = open === i;
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.3, delay: i * 0.03 }} className={cn("glass-card overflow-hidden rounded-xl border border-border", isOpen && "border-amber-gold-500/40")}>
              <button onClick={() => setOpen(isOpen ? null : i)} className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition-colors hover:bg-muted/50">
                <div className="flex-1">
                  {item.category && <div className="mb-1 text-xs font-bold uppercase tracking-wider text-amber-gold-500">{item.category}</div>}
                  <div className="font-rajdhani text-lg font-semibold text-foreground">{item.q}</div>
                </div>
                <ChevronDown className={cn("h-5 w-5 shrink-0 text-muted-foreground transition-transform", isOpen && "rotate-180 text-amber-gold-500")} />
              </button>
              <AnimatePresence>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                    <div className="border-t border-border/50 px-6 py-5 text-sm leading-relaxed text-foreground/80/90">{item.a}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
        {filtered.length === 0 && <div className="glass-card rounded-xl border border-border p-8 text-center text-muted-foreground">No matching items.</div>}
      </div>
    </div>
  );
}
